"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.documentation = exports.signature = exports.name = void 0;
exports.implementation = implementation;
const date_fns_1 = require("date-fns");
const date_fns_tz_1 = require("date-fns-tz");
const error_1 = require("../error");
const constants_1 = require("./constants");
exports.name = "convertDateTime";
exports.signature = "<sss(sl)?(sl)?:s>";
function implementation(str, sourceFormat, targetFormat, 
/**
 * Please mind the `undefined` and `null` values.
 * If someone passes a `null` to this function in JSONata, the language interprets it as provided with `null` value.
 * This means that, if the signature for that parameter is s?, the language will reject this parameter as it is provided.
 * To make sure null values are accepted, we use `(sl)?` instead of `s?` signature.
 */
sourceTimezone, targetTimezone) {
    try {
        if (typeof str !== "string") {
            throw new Error(`parameter str expected type string, received type ${typeof str}`);
        }
        if (typeof sourceFormat !== "string") {
            throw new Error(`parameter sourceFormat expected type string, received type ${typeof sourceFormat}`);
        }
        if (typeof targetFormat !== "string") {
            throw new Error(`parameter targetFormat expected type string, received type ${typeof targetFormat}`);
        }
        /**
         * The `parse` will imply local time zone offset on the input `str` unless it is explicitly specified in the `str`.
         * Example of explicitly specified time zone offset: 2021-01-01T17:09:58-04:00
         *
         * The output of the `parse` function WILL ALWAYS be in a local time zone, no matter what the input is.
         *
         * The `date-fns-tz` does not provide a `parse` function that is time zone aware.
         */
        const inputDateParsed = (0, date_fns_1.parse)(str, sourceFormat, new Date());
        /**
         * Adapted from https://stackoverflow.com/questions/6462578/regex-to-match-all-instances-not-inside-quotes/6464500#6464500
         */
        const isInputWithExplicitOffset = sourceFormat.match(/X(?=([^']*'[^']*')*[^']*$)/g);
        if (sourceTimezone) {
            if (isInputWithExplicitOffset) {
                checkExplicitOffsetAndSourceTimezoneEquality({
                    str,
                    sourceTz: sourceTimezone,
                    inputDateParsed,
                });
            }
        }
        if (targetTimezone) {
            if (isInputWithExplicitOffset) {
                return (0, date_fns_tz_1.formatInTimeZone)(inputDateParsed, targetTimezone, targetFormat);
            }
            const inputDateInUTC = (0, date_fns_tz_1.zonedTimeToUtc)(inputDateParsed, sourceTimezone !== null && sourceTimezone !== void 0 ? sourceTimezone : "UTC");
            return (0, date_fns_tz_1.formatInTimeZone)(inputDateInUTC, targetTimezone, targetFormat);
        }
        /**
         * Because the input has implicit local time zone (see above), but the expectation is to have UTC as the default TZ for the input,
         * we have to treat implicit TZ case differently.
         *
         * The `format` result will be in the local time zone, but the third parameter explicitly overrides the result to be in UTC.
         * We pretend as if it was UTC from the beginning (but in reality all the conversions were made in local time-zone).
         */
        if (!isInputWithExplicitOffset) {
            return (0, date_fns_tz_1.format)(inputDateParsed, targetFormat, { timeZone: "UTC" });
        }
        return (0, date_fns_tz_1.formatInTimeZone)(inputDateParsed, "UTC", targetFormat);
    }
    catch (e) {
        const error = e;
        if (error instanceof RangeError) {
            // This is the only link that is thrown as a part of error's message by date-fns's functions we're using
            // https://github.com/date-fns/date-fns/search?q=RangeError
            error.message = error.message.replace("; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md", "");
        }
        /**
         * We are adding a `code` to mock a native JsonataError, but using our own `code` value
         * to differentiate from the errors defined by Jsonata itself.
         *
         * See existing codes here: https://github.com/jsonata-js/jsonata/blob/65e854d6bfee1d1413ebff7f1a185834c6c42265/src/jsonata.js#L1933
         */
        const stediErrorWithJsonataCode = error;
        const stediErrorMessagePrefix = error_1.CUSTOM_STEDI_JSONATA_ERROR_MESSAGES[error_1.CustomStediJsonataError.STEDI001];
        const functionArguments = [
            `str=\`${str}\``,
            `sourceFormat=\`${sourceFormat}\``,
            `targetFormat=\`${targetFormat}\``,
        ];
        if (sourceTimezone) {
            functionArguments.push(`sourceTimezone=\`${sourceTimezone}\``);
        }
        if (targetTimezone) {
            functionArguments.push(`targetTimezone=\`${targetTimezone}\``);
        }
        const functionArgumentsString = functionArguments.join(", ");
        stediErrorWithJsonataCode.code = error_1.CustomStediJsonataError.STEDI001;
        stediErrorWithJsonataCode.message = `${stediErrorMessagePrefix}: ${error.message}, with arguments: ${functionArgumentsString}`;
        throw stediErrorWithJsonataCode;
    }
}
exports.documentation = {
    label: "$convertDateTime",
    detail: "$convertDateTime(str, sourceFormat, targetFormat [, sourceTimezone [, targetTimezone]])",
    documentation: `Parses the \`str\` according to the \`sourceFormat\` and returns a formatted string according to \`targetFormat\`.
If \`targetTimezone\` is specified, function will also convert date's timezone to desired one. If \`str\` is in format without timezone defined, please use \`sourceTimezone\` to specify its timezone. Without it being specified, function will assume UTC timezone. If both \`str\` and \`sourceTimezone\` will be specified, zone offset in \`str\` will take a precedence over \`sourceTimezone\`.
Both formats must be specified [using unicode tokens](https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table).
${constants_1.STEDI_FUNCTION_DISCLAIMER}

Note there are 4 tokens that might cause confusion:
- \`D\` and \`DD\` that represent the day of a year (1, 2, ..., 365, 366) are often confused with \`d\` and \`dd\` that represent the day of a month (1, 2, ..., 31).
- \`y\` and \`yyyy\` that represent the local week-numbering year (44, 01, 00, 17) are often confused with \`yy\` and \`yyyy\` that represent the calendar year.

On top of the \`$convertDateTime\` function, we also provide popular date/time formats as constants under \`$dateTime\` object:

| Variable                | Value                          |
|-------------------------|--------------------------------|
| $dateTime.RFC3339       | "yyyy-MM-dd'T'HH:mm:ssXXX"     |
| $dateTime.RFC3339Millis | "yyyy-MM-dd'T'HH:mm:ss.SSSXXX" |
| $dateTime.EDIDate       | "yyMMdd"                       |
| $dateTime.EDIDateLong   | "yyyyMMdd"                     |

**Example**

| Function call                                                                                        | Returned value              |
| ---------------------------------------------------------------------------------------------------- | --------------------------- |
| $convertDateTime("20140919", "yyyyMMdd", "yyyy-MM-dd")                                               | "2014-09-19"                |
| $convertDateTime("2021-01-02T12:00:00Z", $dateTime.RFC3339, "yyyy-MM-dd")                            | "2021-01-02"                |
| $convertDateTime("2021-01-02T12:00:00+00:00", $dateTime.RFC3339, "yyyy-MM-dd")                       | "2021-01-02"                |
| $convertDateTime("210102", $dateTime.EDIDate, $dateTime.RFC3339)                                     | "2021-01-02T12:00:00Z"      |
| $convertDateTime("15:00 2nd January 2021", "HH:mm do MMMM yyyy", "yyyy-MM-dd")                       | "2021-01-02"                |
| $convertDateTime("2021-01-01T01:00:00-11:00", $dateTime.RFC3339, $dateTime.RFC3339, null, "UTC")     | "2021-01-01T12:00:00Z"      |
| $convertDateTime("2021-01-01T01:00:00Z", $dateTime.RFC3339, $dateTime.RFC3339, null, "Asia/Bangkok") | "2021-01-01T08:00:00+07:00" |
| $convertDateTime("2021-01-01T01:00:00", "yyyy-MM-dd'T'HH:mm:ss", $dateTime.RFC3339, "EST", "UTC")    | "2021-01-01T06:00:00Z"      |`,
    insertText: "\\$convertDateTime($1, $2, $3)",
    parameters: [
        {
            label: "str",
            documentation: `An input string with date to be formatted`,
        },
        {
            label: "sourceFormat",
            documentation: `Date format of the input string using [using unicode tokens](https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table)`,
        },
        {
            label: "targetFormat",
            documentation: `Format of the output string using [using unicode tokens](https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table)`,
        },
        {
            label: "sourceTimezone",
            documentation: `Specifies source date's timezone if the date is in format without the zone offset e.g.: +01:00 or Z`,
        },
        {
            label: "targetTimezone",
            documentation: `If specified, function will also convert date's timezone to the targeted one.`,
        },
    ],
};
function checkExplicitOffsetAndSourceTimezoneEquality({ inputDateParsed, sourceTz, str, }) {
    var _a, _b;
    const strTimezone = (_b = (_a = str.match(/[+-]\d{2}:\d{2}$/)) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : "UTC";
    const sourceTzOffset = (0, date_fns_tz_1.getTimezoneOffset)(sourceTz, inputDateParsed);
    const strTimezoneOffset = (0, date_fns_tz_1.getTimezoneOffset)(strTimezone);
    if (sourceTzOffset !== strTimezoneOffset) {
        throw new Error(`Timezone offsets specified in \`str\` and \`sourceFormat\` parameter do not match`);
    }
}
